import {
  Shield,
  Factory,
  Users,
  Construction,
  Stethoscope,
  ArrowRight,
} from "lucide-react";
import { nhAlignmentRows, nhDistrictInitiatives } from "@/lib/northHoustonData";

const iconMap = {
  shield: Shield,
  factory: Factory,
  users: Users,
  construction: Construction,
  stethoscope: Stethoscope,
};

export default function StrategicAlignmentNH() {
  return (
    <section
      id="alignment"
      data-testid="section-strategic-alignment-nh"
      className="bg-[#F9F7F1] py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                Strategic Alignment · North Houston District Service Plan
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              We don't compete with the district's plan.<br />
              <em className="italic">We make it possible.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              Bridge Proposal 02 is mapped directly to the{" "}
              <strong className="text-[#0A192F]">North Houston District's</strong>{" "}
              published priorities — Public Safety, Workforce + Community
              Revitalization, Planning &amp; Infrastructure, and Anti-Displacement.
              The District has rebuilt the corridor's infrastructure and recruited
              the employers. Bridge supplies the trained human chain that connects
              long-time residents to the jobs the District is producing.
            </p>
          </div>
        </div>

        {/* Alignment table */}
        <div className="bg-white border border-[#CBD5E1] mb-10">
          <div className="grid grid-cols-12 gap-px bg-[#CBD5E1] border-b border-[#CBD5E1]">
            <div className="col-span-4 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              District Priority
            </div>
            <div className="col-span-6 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              Bridge Model Contribution
            </div>
            <div className="col-span-2 bg-[#0A192F] text-[#F9F7F1] px-5 py-3 font-mono text-[10px] uppercase tracking-widest">
              Strength
            </div>
          </div>
          <ul>
            {nhAlignmentRows.map((r, i) => {
              const Icon = iconMap[r.iconKey] || Factory;
              return (
                <li
                  key={r.priority}
                  data-testid={`alignment-nh-row-${i}`}
                  className={`grid grid-cols-12 border-b border-[#E2E8F0] last:border-b-0 ${
                    r.highlight ? "bg-[#FBF6E5]" : "bg-white"
                  }`}
                >
                  <div className="col-span-4 px-5 py-5 flex items-start gap-3">
                    <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${r.highlight ? "text-[#B8860B]" : "text-[#8B0000]"}`} />
                    <div className="font-display text-base text-[#0A192F] leading-snug">
                      {r.priority}
                    </div>
                  </div>
                  <div className="col-span-6 px-5 py-5 text-sm text-[#475569] leading-relaxed">
                    {r.contribution}
                  </div>
                  <div className="col-span-2 px-5 py-5">
                    <span
                      className={`inline-block font-mono text-[9px] uppercase tracking-widest px-2 py-1 ${
                        r.highlight
                          ? "bg-[#B8860B] text-[#FDF8EF]"
                          : r.strength.startsWith("Direct")
                          ? "bg-[#0A192F] text-[#F9F7F1]"
                          : "bg-[#E2E8F0] text-[#475569]"
                      }`}
                    >
                      {r.strength}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Corridor-momentum callout */}
        <div
          data-testid="alignment-nh-corridor-callout"
          className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-10 mb-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 grain-overlay opacity-20 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-3">
                The Workforce Behind the Corridor
              </div>
              <h3 className="font-display text-2xl lg:text-3xl leading-snug mb-4">
                Every warehouse, ramp, and surgical suite in the corridor requires the people to staff it — and the families to live in it.
              </h3>
              <p className="text-[#CBD5E1] leading-relaxed text-sm lg:text-base">
                The District has executed 20M+ sq ft of industrial absorption,
                anchored by Amazon, FedEx, Sysco, Coca-Cola, and 2025 entrants
                like Panelmatic (1,000 expected hires) and Seojin Global. The
                infrastructure pipeline — North Corridor Consolidation Pkg 3
                (Fall 2025 → Winter 2028) and IAH's International Central
                Processor — adds more workforce demand across construction,
                aviation, allied health, and light manufacturing.
              </p>
              <p className="text-[#CBD5E1] leading-relaxed text-sm lg:text-base mt-3">
                <strong className="text-[#F9F7F1]">
                  Bridge produces that workforce.
                </strong>{" "}
                From district residents. Through Lone Star North Harris and HCC
                North. Without displacing the population that weathered
                Greenspoint into corridor.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-[#F9F7F1]/5 border border-[#F9F7F1]/15 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">
                  Active in the District · 2025
                </div>
                <ul className="space-y-2">
                  {nhDistrictInitiatives.map((s) => (
                    <li key={s} className="flex items-start gap-2 text-xs text-[#CBD5E1] leading-relaxed">
                      <span className="text-[#D4AF37] shrink-0 mt-0.5">▸</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-[#94A3B8] italic mt-4 leading-relaxed">
                  Bridge does not replace any of these initiatives. Bridge
                  makes them work for District families.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Cover letter download */}
      </div>
    </section>
  );
}
